// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import IsNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { SelectPresentationProps } from "@synd/MobiusOverrides/Select";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@synd/MobiusOverrides/TextField/TextField";
import Tooltip, { TooltipPresentationProps } from "@synd/MobiusOverrides/Tooltip/Tooltip";
import TooltipIcon from "@synd/XDL/Icons/NotificationInfoCircle";
import IconStepperSubtract from "@synd/XDL/Icons/QuantityStepperMinus";
import IconStepperAdd from "@synd/XDL/Icons/QuantityStepperPlus";
import { mergeWithCss, xDefaultRules, xMask } from "@synd/XDL/Utilities";
import React, { useEffect, useMemo, useState } from "react";

// XNG-Changes
export type QtyFormFieldSizeVariant = "large" | "default";

interface OwnProps extends Omit<TextFieldProps, "css"> {
    onDecreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    onIncreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    showActionButton?: boolean;
    extendedStyles?: QtyFieldStyles;
    allowZero?: boolean;
    editable?: boolean;
    showLabel?: boolean;
    showMultipleSaleQtyTooltip?: boolean;
    min?: number | string;
    max?: number | string;
    validationRules?: string;
    minimumOrderQty?: number;
    multipleSaleQty?: number;
    showMultipleSaleTooltip?: boolean;
    // XNG-changes
    qtySizeVariant?: QtyFormFieldSizeVariant;
}

type Props = OwnProps;

export interface QtyFieldStyles {
    container?: InjectableCss;
    editableQuantityTextField?: TextFieldPresentationProps;
    wrapper?: InjectableCss;
    qtyLabel?: TypographyProps;
    valueText?: TypographyProps;
    actionButton?: ClickablePresentationProps;
    questionIconStyles?: IconProps;
    multipleSaleQtyWrapper?: InjectableCss;
    multipleSaleQtyToolTip?: TooltipPresentationProps;
    print?: InjectableCss;
    qtyLabelNotEditable?: TypographyProps;
    tooltipBody?: InjectableCss;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    qtyLabelContainer?: InjectableCss;
    // XNG-Changes
    tooltipTextWrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const qtyFieldStyles: QtyFieldStyles = {};

const enterKey = 13;

const QtyField = ({
    extendedStyles,
    onIncreaseChangeHandler,
    onDecreaseChangeHandler,
    disabled: disabledProps = false,
    value: currentValue = 1,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    label: labelOverride,
    showActionButton = true,
    allowZero = false,
    editable = true,
    showLabel = true,
    showMultipleSaleQtyTooltip = false,
    min = 0,
    max = 999999,
    minimumOrderQty,
    multipleSaleQty,
    showMultipleSaleTooltip = true,
    validationRules,
    // XNG-Changes
    qtySizeVariant = "default",
    ...qtyFieldsProps
}: Props) => {
    const styles = useMemo(() => mergeWithCss(qtyFieldStyles, extendedStyles), []);
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);

    let minQty = 0;
    let maxQty = 999999;
    (validationRules || xDefaultRules["qty"]).split("|").forEach(item => {
        const [attr, val] = item.split(":");
        if (attr === "min") {
            minQty = Math.max(Number(min), +val);
        }
        if (attr === "max") {
            maxQty = Math.min(Number(max), +val);
        }
    }, {});

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            onBlurHandler();
            setCallOnSubmitSafety(false);
        }
    }, [currentValue]);
    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.target.value = xMask.qty(event.target.value);
        onChangeTextField?.(event);
    };

    useEffect(() => {
        if (minQty > (currentValue as number) && !allowZero) {
            onChangeHandler?.({
                target: { value: minQty.toString() },
                currentTarget: { value: minQty.toString() },
            } as any);
        }
    }, [minQty, maxQty]);

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey && !onKeyDownTextField) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
        onKeyDownTextField?.(event);
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        if (event?.currentTarget) {
            event.currentTarget.value = xMask.qty(event.currentTarget.value);
            setTimeout(() => {
                event.currentTarget?.setSelectionRange(0, 0);
            }, 0);
            onChangeHandler(event);
        }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        let nextValue;
        // do rounding in case we have minimumOrderedQuantity and MultipleSaleQty
        if (multipleSaleQty && multipleSaleQty > 1) {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - multipleSaleQty >= minQty
                    ? Number(currentValue) - multipleSaleQty
                    : multipleSaleQty;
        } else {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - 1 >= minQty
                    ? Number(currentValue) - 1
                    : undefined;
        }

        if (allowZero && nextValue === undefined) {
            return;
            // eslint-disable-next-line no-else-return
        } else if (!allowZero && !nextValue) {
            return;
        }

        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onDecreaseChangeHandler) {
            event = onDecreaseChangeHandler(event);
        }

        setCallOnSubmitSafety(true);
        onChangeHandler(event as any);
    };

    const onIncreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        let nextValue;
        // do rounding in case we have minimumOrderedQuantity and MultipleSaleQty
        if (multipleSaleQty && multipleSaleQty > 1) {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) + multipleSaleQty <= maxQty
                    ? Number(currentValue) + multipleSaleQty
                    : undefined;
        } else {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) + 1 <= maxQty
                    ? Number(currentValue) + 1
                    : undefined;
        }

        if (!nextValue) {
            return;
        }

        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onIncreaseChangeHandler) {
            event = onIncreaseChangeHandler(event);
        }
        setCallOnSubmitSafety(true);
        onChangeHandler(event as any);
    };

    const multipleSaleTooltip = showMultipleSaleQtyTooltip && (
        <StyledWrapper {...styles.multipleSaleQtyWrapper}>
            <Tooltip
                text={
                    <StyledWrapper {...styles.tooltipTextWrapper}>
                        {!!multipleSaleQty && multipleSaleQty > 1 && (
                            <>{`${siteMessage("Custom_MultipleSaleQtyMsg", "Multiple Sale Quantity").toString()} ${multipleSaleQty}`}</>
                        )}{" "}
                        {!!minimumOrderQty && minimumOrderQty > 1 && (
                            <>{`${siteMessage("Custom_MinimumOrderQtyMsg", "Minimum Order Quantity").toString()} ${minimumOrderQty}`}</>
                        )}
                    </StyledWrapper>
                }
                triggerComponent={<Icon {...styles.questionIconStyles} src={TooltipIcon} />}
                {...styles.multipleSaleQtyToolTip}
            />
        </StyledWrapper>
    );

    if (showActionButton && editable) {
        return (
            <StyledWrapper {...styles.container} data-test-selector="qty-field">
                {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                <StyledWrapper {...styles.qtyLabelContainer}>
                    <VisuallyHidden as="label">{translate("QTY")}</VisuallyHidden>
                    {/* XNG-Changes */}
                    <StyledWrapper {...styles.wrapper} {...{ qtySizeVariant }}>
                        <Clickable
                            {...styles.actionButton}
                            disabled={disabled || Number(currentValue) <= minQty}
                            onClick={onDecreaseClickHandler}
                            data-test-selector="product_qtyOrdered_decrease"
                            aria-label="decrease quantity"
                            className="qty-field-decrease"
                        >
                            <Icon src={IconStepperSubtract} size={8} />
                        </Clickable>
                        <TextField
                            type="qty"
                            disabled={disabled}
                            value={currentValue}
                            onChange={onChangeHandler}
                            onBlur={onBlurHandler}
                            onClick={onClickHandler}
                            onKeyDown={onKeyDownHandler}
                            aria-label="qty"
                            validationRules={validationRules}
                            {...styles.editableQuantityTextField}
                            {...qtyFieldsProps}
                        />
                        <Clickable
                            {...styles.actionButton}
                            disabled={disabled || Number(currentValue) >= maxQty}
                            onClick={onIncreaseClickHandler}
                            data-test-selector="product_qtyOrdered_increase"
                            aria-label="increase quantity"
                        >
                            <Icon src={IconStepperAdd} size={8} />
                        </Clickable>
                    </StyledWrapper>

                    {/* Add multiple qty tooltip */}
                    {showMultipleSaleTooltip && multipleSaleTooltip}
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    if (editable) {
        return (
            <>
                <VisuallyHidden as="label">{labelOverride || translate("QTY")}</VisuallyHidden>
                {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                <TextField
                    type="qty"
                    disabled={disabled}
                    value={currentValue}
                    onChange={onChangeHandler}
                    onBlur={onBlurHandler}
                    onClick={onClickHandler}
                    onKeyDown={onKeyDownHandler}
                    {...styles.editableQuantityTextField}
                    {...qtyFieldsProps}
                />
            </>
        );
    }

    return (
        <>
            <VisuallyHidden as="label">{labelOverride || translate("QTY")}</VisuallyHidden>
            {showLabel && <Typography {...styles.qtyLabelNotEditable}>{labelOverride || translate("QTY")}</Typography>}
            <Typography {...styles.valueText}>
                {currentValue} {showMultipleSaleTooltip && multipleSaleTooltip}
            </Typography>
        </>
    );
};

export default QtyField;
